<template>
    <modal id="employee-work-shift-modal"
           size="large"
           v-model="showModal"
           :title="$t('change_work_shift')"
           @submit="submit"
           :loading="loading"
           :preloader="preloader">

        <div class="form-group d-flex flex-column flex-md-row align-items-md-center mb-4">
            <label class="mr-md-3 mb-2 mb-md-0">{{ $t('shift_type') }}</label>
            <app-input
                type="radio"
                :list="[
                    {id: 'existing', value: $t('pick_an_existing_shift')},
                    {id: 'custom', value: $t('set_custom_timing')}
                ]"
                v-model="formData.mode"
            />
        </div>

        <!-- Existing shift -->
        <template v-if="formData.mode === 'existing'">
            <app-form-group
                :label="$t('work_shift')"
                type="select"
                :list="shifts"
                list-value-field="name"
                v-model="formData.working_shift_id"
                :placeholder="$t('select_a_work_shift')"
                :error-message="$errorMessage(errors, 'working_shift_id')"
            />
            <app-note v-if="selectedShiftName" class="mb-0" :title="$t('note')"
                      :notes="$t('employee_will_be_moved_to_this_shift_from_today')"/>
        </template>

        <!-- Custom timing -->
        <template v-else>
            <app-form-group
                :label="$t('name')"
                type="text"
                v-model="formData.name"
                :placeholder="employeePlaceholderName"
                :error-message="$errorMessage(errors, 'name')"
            />
            <div class="row">
                <div class="col-md-6">
                    <div class="form-group">
                        <label>{{ $t('start_time') }}</label>
                        <app-input
                            type="time"
                            v-model="formData.start_at"
                            :error-message="$errorMessage(errors, 'start_at')"
                        />
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="form-group">
                        <label>{{ $t('end_time') }}</label>
                        <app-input
                            type="time"
                            v-model="formData.end_at"
                            :error-message="$errorMessage(errors, 'end_at')"
                        />
                    </div>
                </div>
            </div>

            <div class="mb-2">
                <h6 class="text-danger">{{ $t('select_your_weekend_days') }}</h6>
            </div>
            <app-input
                type="checkbox"
                :list="weekdays"
                v-model="formData.weekdays"
                radio-checkbox-wrapper="row"
                custom-checkbox-type="checkbox-default col-md-3"
            />
            <p class="text-muted mb-0 mt-2">{{ $t('the_same_time_applies_to_every_working_day') }}</p>
        </template>
    </modal>
</template>

<script>
import ModalMixin from "../../../../../../common/Mixin/Global/ModalMixin";
import {axiosGet, axiosPatch} from "../../../../../../common/Helper/AxiosHelper";
import {toWallClockTime, wallClockToDisplay} from "../../../../../../common/Helper/Support/DateTimeHelper";
import {EMPLOYEES} from "../../../../../Config/ApiUrl";

export default {
    name: "WorkShiftChangeModal",
    mixins: [ModalMixin],
    props: {
        employeeId: {required: true},
        employeeName: {default: ''},
    },
    data() {
        return {
            preloader: true,
            loading: false,
            errors: {},
            shifts: [],
            weekdays: [
                {id: "sun", value: this.$t("sunday")},
                {id: "mon", value: this.$t("monday")},
                {id: "tue", value: this.$t("tuesday")},
                {id: "wed", value: this.$t("wednesday")},
                {id: "thu", value: this.$t("thursday")},
                {id: "fri", value: this.$t("friday")},
                {id: "sat", value: this.$t("saturday")},
            ],
            formData: {
                mode: 'existing',
                working_shift_id: '',
                name: '',
                start_at: '',
                end_at: '',
                weekdays: [],
            },
        }
    },
    computed: {
        url() {
            return `${EMPLOYEES}/${this.employeeId}/working-shift`;
        },
        selectedShiftName() {
            const s = this.shifts.find(s => s.id === this.formData.working_shift_id);
            return s ? s.name : '';
        },
        employeePlaceholderName() {
            return this.employeeName ? `${this.employeeName} — ${this.$t('custom_shift')}` : this.$t('custom_shift');
        }
    },
    created() {
        this.fetchData();
    },
    methods: {
        fetchData() {
            axiosGet(this.url).then(({data}) => {
                this.shifts = data.shifts || [];
                const current = data.working_shift;

                if (current && current.is_personal) {
                    // Prefill the custom editor from the employee's personal shift.
                    this.formData.mode = 'custom';
                    this.formData.name = current.name;
                    const working = (current.details || []).find(d => !parseInt(d.is_weekend) && d.start_at && d.end_at);
                    if (working) {
                        this.formData.start_at = wallClockToDisplay(working.start_at);
                        this.formData.end_at = wallClockToDisplay(working.end_at);
                    }
                    this.formData.weekdays = (current.details || [])
                        .filter(d => parseInt(d.is_weekend))
                        .map(d => d.weekday);
                } else if (current) {
                    this.formData.mode = 'existing';
                    this.formData.working_shift_id = current.id;
                }
            }).finally(() => this.preloader = false);
        },
        buildDetails() {
            const allDays = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
            const startUtc = toWallClockTime(this.formData.start_at);
            const endUtc = toWallClockTime(this.formData.end_at);

            return allDays.map(weekday => {
                const isWeekend = this.formData.weekdays.includes(weekday);
                return {
                    weekday,
                    start_at: isWeekend ? null : startUtc,
                    end_at: isWeekend ? null : endUtc,
                    is_weekend: isWeekend ? 1 : 0,
                };
            });
        },
        submit() {
            this.loading = true;
            this.errors = {};

            const payload = this.formData.mode === 'existing'
                ? {mode: 'existing', working_shift_id: this.formData.working_shift_id}
                : {mode: 'custom', name: this.formData.name, details: this.buildDetails()};

            axiosPatch(this.url, payload).then(({data}) => {
                this.$toastr.s('', data.message);
                this.$emit('shift-updated');
                $('#employee-work-shift-modal').modal('hide');
            }).catch(({response}) => {
                if (response && response.status === 422) {
                    this.errors = response.data.errors || {};
                }
                this.$toastr.e('', response?.data?.message || this.$t('something_went_wrong'));
            }).finally(() => this.loading = false);
        }
    }
}
</script>
